import React, { useState, useCallback, useRef, useEffect } from 'react';

/** Describes why a card matched the search query */
export interface MatchContext {
  readonly field: 'title' | 'number' | 'label' | 'member' | 'custom-field';
  readonly value: string;
}

export interface SearchResult {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly listName: string;
  readonly labels: readonly string[];
  readonly matchContext: MatchContext;
  readonly score: number;
}

export interface BoardSearchProps {
  readonly onSearch: (query: string) => Promise<SearchResult[]>;
  readonly onCardSelect?: (cardId: string) => void;
  readonly onAdvancedClick?: (currentQuery: string) => void;
}

/** Debounce delay in milliseconds */
const DEBOUNCE_MS = 250;

/**
 * Formats a match context field into a user-friendly label.
 */
function formatMatchField(field: MatchContext['field']): string {
  switch (field) {
    case 'title': return 'Title';
    case 'number': return 'Card #';
    case 'label': return 'Label';
    case 'member': return 'Member';
    case 'custom-field': return 'Field';
  }
}

/**
 * Highlights matching tokens within text by wrapping them in <mark> elements.
 */
function HighlightedText({ text, query }: { text: string; query: string }): React.ReactElement {
  if (!query.trim()) return <>{text}</>;

  const tokens = query.toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return <>{text}</>;

  // Build a regex that matches any token (escaped for safety)
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');

  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => {
        const isMatch = tokens.some((t) => part.toLowerCase() === t);
        return isMatch ? (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 text-inherit rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

/**
 * Board search — multi-token matching with ranked results, debounced input,
 * match highlighting, and rich result display (card number, labels, match context).
 * Requires minimum 2 characters to initiate search.
 * Clicking a result or pressing Enter navigates to that card.
 */
export function BoardSearch({ onSearch, onCardSelect, onAdvancedClick }: BoardSearchProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search effect
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const r = await onSearch(query.trim());
        setResults(r);
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, onSearch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setActiveIndex(-1);
  };

  const selectCard = useCallback((cardId: string) => {
    onCardSelect?.(cardId);
    setQuery('');
    setResults([]);
    setActiveIndex(-1);
  }, [onCardSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          const card = results[activeIndex];
          if (card) selectCard(card.cardId);
        } else if (results.length === 1) {
          selectCard(results[0]!.cardId);
        }
        break;
      case 'Escape':
        setQuery('');
        setResults([]);
        setActiveIndex(-1);
        break;
    }
  }, [results, activeIndex, selectCard]);

  return (
    <div role="search" aria-label="Search cards" className="relative">
      <label htmlFor="board-search" className="sr-only">Search cards</label>
      <div className="flex items-center gap-1">
        <input
          id="board-search"
          type="search"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Search cards, labels, members..."
          aria-label="Search cards"
          aria-expanded={results.length > 0}
          aria-controls="board-search-results"
          aria-activedescendant={activeIndex >= 0 ? `search-result-${activeIndex}` : undefined}
          className="w-full rounded-md bg-white/20 border border-white/30 px-3 py-1.5 text-sm text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30"
        />
        {onAdvancedClick && (
          <button
            type="button"
            onClick={() => onAdvancedClick(query)}
            className="shrink-0 px-2 py-1.5 text-[11px] text-white/70 hover:text-white border border-white/30 rounded-md hover:bg-white/10 transition-colors"
            aria-label="Advanced search"
            title="Advanced search (Ctrl+Shift+F)"
          >
            Advanced
          </button>
        )}
      </div>

      {isSearching && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg px-3 py-2">
          <p className="text-xs text-muted-foreground">Searching...</p>
        </div>
      )}

      {!isSearching && results.length > 0 && (
        <ul
          id="board-search-results"
          ref={listRef}
          role="listbox"
          aria-label="Search results"
          className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-80 overflow-y-auto"
        >
          {results.map((r, index) => (
            <li
              key={r.cardId}
              id={`search-result-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => selectCard(r.cardId)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`px-3 py-2 cursor-pointer transition-colors border-b border-border last:border-b-0 ${
                index === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
              }`}
            >
              {/* Title row with card number */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground font-mono shrink-0">
                  #{r.cardNumber}
                </span>
                <span className="text-sm font-medium text-card-foreground truncate">
                  <HighlightedText text={r.title} query={query} />
                </span>
              </div>

              {/* Meta row: list + labels + match context */}
              <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                <span className="shrink-0">in {r.listName}</span>
                {r.labels.length > 0 && (
                  <span className="truncate">
                    · {r.labels.slice(0, 3).join(', ')}
                    {r.labels.length > 3 ? ` +${r.labels.length - 3}` : ''}
                  </span>
                )}
              </div>

              {/* Match context (what matched, if not the title) */}
              {r.matchContext.field !== 'title' && (
                <div className="mt-0.5 text-xs text-muted-foreground/70 italic truncate">
                  Matched {formatMatchField(r.matchContext.field)}: <HighlightedText text={r.matchContext.value} query={query} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {query.trim().length >= 2 && !isSearching && results.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg px-3 py-2">
          <p className="text-xs text-muted-foreground">No results found.</p>
        </div>
      )}
    </div>
  );
}
